import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cx } from '../lib/cx';

export interface SeriesChartPoint {
  /** ISO dan — koristi se kao ključ, ne kao natpis. */
  day: string;
  value: number;
}

export interface SeriesChartProps {
  points: readonly SeriesChartPoint[];
  ariaLabel: string;
  unit?: string;
  /** Natpis osi X; prima ISO dan, vraća kratki oblik za lokalizaciju. */
  formatDay: (day: string) => string;
  formatValue: (value: number) => string;
  height?: number;
  className?: string;
}

const PAD = { top: 18, right: 18, bottom: 30, left: 48 };
const FALLBACK_WIDTH = 640;

/**
 * Širina se MJERI, ne skalira. `preserveAspectRatio="none"` bi rastegnuo i tekst
 * i debljinu linije, pa graf crtamo u stvarnim pikselima kontejnera.
 */
function useElementWidth(fallback: number): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(fallback);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => {
      const next = node.getBoundingClientRect().width;
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/** Osne oznake na okruglim brojevima — inače os čita kao slučajni niz decimala. */
function niceTicks(min: number, max: number): number[] {
  if (min === max) return [min];
  const span = max - min;
  const rawStep = span / 2;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rawStep) ?? magnitude * 10;
  const start = Math.floor(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step / 2; v += step) {
    if (v >= min - step / 2) ticks.push(Number(v.toFixed(6)));
  }
  return ticks.length >= 2 ? ticks : [min, max];
}

/**
 * Jedna serija kroz vrijeme — inline SVG, bez chart biblioteke
 * (ARHITEKTURA.md sekcija 11). Boje dolaze isključivo iz tokena preko
 * `text-accent` / `text-line` / `fill-ink-muted`.
 *
 * Jedna serija ne dobiva legendu: naslov kartice već imenuje što je nacrtano.
 * Vrijednost se ispisuje samo na zadnjoj točki; ostalo nose os, tooltip i tablica.
 */
export function SeriesChart({
  points,
  ariaLabel,
  unit,
  formatDay,
  formatValue,
  height = 240,
  className,
}: SeriesChartProps) {
  const gradientId = useId();
  const [wrapRef, width] = useElementWidth(FALLBACK_WIDTH);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);

  const W = Math.max(280, width);
  const H = height;

  const values = points.map((p) => p.value);
  const minValue = values.length ? Math.min(...values) : 0;
  const maxValue = values.length ? Math.max(...values) : 1;
  const span = maxValue - minValue || Math.abs(maxValue) || 1;
  const lo = minValue - span * 0.12;
  const hi = maxValue + span * 0.12;

  const px = useCallback(
    (i: number) =>
      points.length <= 1
        ? PAD.left + (W - PAD.left - PAD.right) / 2
        : PAD.left + (i / (points.length - 1)) * (W - PAD.left - PAD.right),
    [points.length, W],
  );
  const py = useCallback(
    (v: number) => PAD.top + (1 - (v - lo) / (hi - lo)) * (H - PAD.top - PAD.bottom),
    [H, hi, lo],
  );

  const nearestIndex = useCallback(
    (clientX: number): number | null => {
      const svg = svgRef.current;
      if (!svg || points.length === 0) return null;
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return null;
      const x = clientX - rect.left;
      let best = 0;
      let bestDistance = Infinity;
      for (let i = 0; i < points.length; i += 1) {
        const distance = Math.abs(px(i) - x);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      return best;
    },
    [points.length, px],
  );

  useEffect(() => {
    setCursor(null);
  }, [points]);

  if (points.length === 0) return null;

  const line = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(p.value).toFixed(1)}`)
    .join(' ');
  const baseline = H - PAD.bottom;
  const area = `${line} L${px(points.length - 1).toFixed(1)},${baseline} L${px(0).toFixed(1)},${baseline} Z`;

  const ticks = niceTicks(lo, hi).filter((t) => t >= lo && t <= hi);
  const last = points[points.length - 1]!;
  const active = cursor !== null ? points[cursor] : null;

  const tooltipLeft = cursor !== null ? Math.min(Math.max(px(cursor), 70), W - 70) : 0;

  return (
    <figure className={cx('m-0 w-full', className)}>
      <div ref={wrapRef} className="relative w-full">
        <svg
          ref={svgRef}
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          className="block w-full touch-pan-y"
          onPointerMove={(e) => setCursor(nearestIndex(e.clientX))}
          onPointerLeave={() => setCursor(null)}
          onFocus={() => setCursor(points.length - 1)}
          onBlur={() => setCursor(null)}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            e.preventDefault();
            setCursor((c) => {
              const from = c ?? points.length - 1;
              const next = e.key === 'ArrowLeft' ? from - 1 : from + 1;
              return Math.max(0, Math.min(points.length - 1, next));
            });
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.14" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Mreža je povučena, puna i tanka — nikad crtkana. */}
          {ticks.map((tick) => (
            <g key={tick} className="text-line">
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={py(tick)}
                y2={py(tick)}
                stroke="currentColor"
                strokeWidth="1"
              />
              <text
                x={PAD.left - 10}
                y={py(tick) + 4}
                textAnchor="end"
                fontSize="11"
                className="fill-ink-muted tabular-nums"
              >
                {formatValue(tick)}
              </text>
            </g>
          ))}

          <g className="text-accent">
            <path d={area} fill={`url(#${gradientId})`} />
            {points.length > 1 && (
              <path
                d={line}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
            {/* Zadnja točka nosi prsten u boji podloge da ostane čitljiva na liniji. */}
            <circle
              cx={px(points.length - 1)}
              cy={py(last.value)}
              r="4.5"
              fill="currentColor"
              strokeWidth="2"
              className="stroke-surface"
            />
          </g>

          {active && cursor !== null && (
            <g>
              <line
                x1={px(cursor)}
                x2={px(cursor)}
                y1={PAD.top}
                y2={baseline}
                strokeWidth="1"
                className="stroke-line-strong"
              />
              <circle
                cx={px(cursor)}
                cy={py(active.value)}
                r="5"
                className="fill-accent stroke-surface"
                strokeWidth="2"
              />
            </g>
          )}

          <text
            x={PAD.left}
            y={H - 8}
            fontSize="11"
            textAnchor="start"
            className="fill-ink-muted"
          >
            {formatDay(points[0]!.day)}
          </text>
          {points.length > 1 && (
            <text
              x={W - PAD.right}
              y={H - 8}
              fontSize="11"
              textAnchor="end"
              className="fill-ink-muted"
            >
              {formatDay(last.day)}
            </text>
          )}
        </svg>

        {active && cursor !== null && (
          <div
            role="status"
            aria-live="polite"
            style={{ left: tooltipLeft, top: 4 }}
            className={cx(
              'pointer-events-none absolute -translate-x-1/2 rounded-md border border-line-subtle',
              'bg-surface px-3 py-2 text-center shadow-card-raised',
            )}
          >
            <span className="block font-display text-[16px] font-semibold leading-none text-ink tabular-nums">
              {formatValue(active.value)}
              {unit ? <span className="ml-1 font-body text-[11px] text-ink-muted">{unit}</span> : null}
            </span>
            <span className="mt-1 block text-[11px] text-ink-muted">{formatDay(active.day)}</span>
          </div>
        )}
      </div>

      <figcaption className="sr-only">
        {ariaLabel}
        {unit ? ` (${unit})` : ''}
      </figcaption>
    </figure>
  );
}
